// Edit-mode wrapper around each page section: a bar to move, hide or delete
// it, and "Add section" buttons between sections. Renders nothing extra on the
// public site.
import { ReactNode } from 'react';
import { FaArrowDown, FaArrowUp, FaEye, FaEyeSlash, FaPaintBrush, FaPlus, FaTrash } from 'react-icons/fa';
import { useSite } from '../content/context';
import { CUSTOM_TYPES, CustomType, isBuiltIn, newSection, Section, SECTION_TYPE_INFO } from '../content/layout';
import { ActionButton } from '../edit/primitives';
import { useSections } from './useSections';
import './sections.css';

export function SectionFrame({ section, index, children }: { section: Section; index: number; children: ReactNode }) {
  const { edit } = useSite();
  const { sections, move, remove, updateSection } = useSections();
  if (!edit) return <>{children}</>;

  const info = SECTION_TYPE_INFO[section.type];
  const name = section.title && !isBuiltIn(section.type) ? section.title : info.label;

  return (
    <section className={`af-section-frame${section.hidden ? ' af-section-hidden' : ''}`} aria-label={`${info.label} section`}>
      <div className="af-section-bar">
        <span className="af-section-name">
          {info.label}
          {section.hidden ? <span className="af-section-badge">Hidden from visitors</span> : null}
        </span>
        <span className="af-section-tools">
          {edit.openSectionStyle ? (
            <ActionButton label={`Style ${name} section`} icon={<FaPaintBrush />} showLabel onClick={() => edit.openSectionStyle?.(section.id)} />
          ) : null}
          <ActionButton label={`Move ${name} section up`} icon={<FaArrowUp />} onClick={() => move(index, index - 1)} disabled={index === 0} />
          <ActionButton label={`Move ${name} section down`} icon={<FaArrowDown />} onClick={() => move(index, index + 1)} disabled={index === sections.length - 1} />
          <ActionButton
            label={section.hidden ? `Show ${name} section` : `Hide ${name} section`}
            icon={section.hidden ? <FaEye /> : <FaEyeSlash />}
            showLabel
            onClick={() => updateSection(section.id, { hidden: !section.hidden })}
          />
          {isBuiltIn(section.type) ? null : (
            <ActionButton
              label={`Delete ${name} section`}
              icon={<FaTrash />}
              danger
              onClick={() => {
                if (window.confirm(`Delete the "${name}" section and everything in it? You can undo this until you publish.`)) remove(index);
              }}
            />
          )}
        </span>
      </div>
      <div className="af-section-body">{children}</div>
    </section>
  );
}

/** "+ Add section" between sections; asks for the type, then inserts it. */
export function AddSectionButton({ index }: { index: number }) {
  const { edit } = useSite();
  const { insert } = useSections();
  if (!edit) return null;

  const add = async () => {
    const result = await edit.openForm({
      title: 'Add a section',
      fields: [
        {
          name: 'type',
          label: 'What kind of section?',
          type: 'select',
          required: true,
          options: CUSTOM_TYPES.map((type) => ({ value: type, label: `${SECTION_TYPE_INFO[type].label}: ${SECTION_TYPE_INFO[type].description}` })),
        },
        { name: 'title', label: 'Heading', placeholder: 'e.g. MERCH', hint: 'You can change it later by clicking it on the page.' },
      ],
      values: { type: 'text' },
      submitLabel: 'Add section',
    });
    if (!result || !('values' in result)) return;
    const type = result.values.type as CustomType;
    if (!CUSTOM_TYPES.includes(type)) return;
    insert(index, newSection(type, result.values.title.trim()));
  };

  return (
    <div className="af-add-section">
      <button type="button" className="af-add-section-btn" onClick={add}>
        <FaPlus aria-hidden="true" /> Add section here
      </button>
    </div>
  );
}
